import React, { useState } from 'react';
import {
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  Box,
  Chip,
  Rating,
  IconButton,
  Button,
  Avatar,
  Tooltip,
  Badge,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Favorite,
  FavoriteBorder,
  ShoppingCart,
  Visibility,
  Share,
  LocationOn,
  Store,
  Message,
  PhotoCamera,
  LocalOffer,
  NewReleases,
  Sell,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import JobCard from './JobCard';
import AuctionCard from './AuctionCard';
import NewOfferPopup from './NewOfferPopup';
import { useTranslation } from '../../contexts/TranslationContext';
import { useNavigate } from 'react-router-dom';

interface ProductCardProps {
  product: {
    id: string;
    name: string;
    price: number;
    originalPrice?: number;
    image: string;
    rating: number;
    reviewCount: number;
    category: string;
    sellerId?: string; // ID of the user who posted the product/job
    seller: {
      name: string;
      avatar: string;
      location: string;
    };
    isNew?: boolean;
    isFeatured?: boolean;
    discount?: number;
    description?: string;
    tags?: string[];
    isJob?: boolean;
    // Job-specific fields
    company?: string;
    companyLogo?: string;
    location?: string;
    salary?: {
      min: number;
      max: number;
      currency: string;
    };
    employmentType?: string;
    experienceLevel?: string;
    isRemote?: boolean;
    postedDate?: string;
    requirements?: string[];
    benefits?: string[];
    applicantsCount?: number;
    viewsCount?: number;
    isUrgent?: boolean;
    // Auction-specific fields
    auctionId?: string;
    auction?: {
      id: string;
      end_time: string;
      current_bid: number;
      bid_increment: number;
      status: string;
      reserve_price?: number;
      bid_count: number;
      highest_bidder?: {
        name: string;
        avatar?: string;
      };
    };
    // Private single buy flag
    is_private_singlebuy?: boolean;
    // Negotiable flag
    isNegotiable?: boolean;
    // DB product_type: d=Direktkauf, a=Auktion, p=Angebot
    productType?: 'd' | 'a' | 'p';
  };
  onAddToCart?: (productId: string) => void;
  onToggleFavorite?: (productId: string) => void;
  onViewDetails?: (productId: string, navigationState?: any) => void;
  onShare?: (productId: string) => void;
  isFavorite?: boolean;
  isOwnProduct?: boolean;
  variant?: 'default' | 'compact' | 'detailed';
  showSeller?: boolean;
  navigationState?: {
    currentPage: number;
    filters: any;
    searchQuery: string;
    sortBy: string;
    viewMode: string;
  };
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  onAddToCart,
  onToggleFavorite,
  onViewDetails,
  onShare,
  isFavorite = false,
  isOwnProduct = false,
  variant = 'default',
  showSeller = true,
  navigationState,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [offerPopupOpen, setOfferPopupOpen] = useState(false);

  const pt = (product as any).productType ?? (product as any).product_type;
  const isNegotiable = Boolean((product as any).isNegotiable ?? (product as any).is_negotiable);
  const isOfferProduct = pt === 'p' || isNegotiable || (!pt && Boolean(product.is_private_singlebuy));

  const purchaseTypeUi = (() => {
    const base = {
      color: '#1f2937',
      bgcolor: 'linear-gradient(135deg, rgba(255,210,77,1) 0%, rgba(255,152,0,1) 100%)',
      icon: <Sell sx={{ fontSize: 16, color: '#1f2937' }} />,
    };

    if (pt === 'a') {
      return {
        label: 'Auktion',
        ...base,
        bgcolor: 'linear-gradient(135deg, rgba(124,58,237,1) 0%, rgba(59,130,246,1) 100%)',
        icon: <Sell sx={{ fontSize: 16, color: 'white' }} />,
        color: 'white',
      };
    }
    if (pt === 'p') {
      return {
        label: 'Angebot',
        ...base,
        bgcolor: 'linear-gradient(135deg, rgba(102,126,234,1) 0%, rgba(118,75,162,1) 100%)',
        icon: <Message sx={{ fontSize: 16, color: 'white' }} />,
        color: 'white',
      };
    }
    if (pt === 'd') {
      if (isNegotiable) {
        return {
          label: 'Verhandlung',
          ...base,
          bgcolor: 'linear-gradient(135deg, rgba(245,158,11,1) 0%, rgba(251,113,133,1) 100%)',
          icon: <Sell sx={{ fontSize: 16, color: 'white' }} />,
          color: 'white',
        };
      }
      return {
        label: 'Direktkauf',
        ...base,
        icon: <Sell sx={{ fontSize: 16, color: '#1f2937' }} />,
      };
    }

    if (Boolean(product.is_private_singlebuy)) {
      return {
        label: 'Angebot',
        ...base,
        bgcolor: 'linear-gradient(135deg, rgba(102,126,234,1) 0%, rgba(118,75,162,1) 100%)',
        icon: <Message sx={{ fontSize: 16, color: 'white' }} />,
        color: 'white',
      };
    }
    if (isNegotiable) {
      return {
        label: 'Verhandlung',
        ...base,
        bgcolor: 'linear-gradient(135deg, rgba(245,158,11,1) 0%, rgba(251,113,133,1) 100%)',
        icon: <Sell sx={{ fontSize: 16, color: 'white' }} />,
        color: 'white',
      };
    }
    return {
      label: 'Direktkauf',
      ...base,
    };
  })();
  // If this is an auction, use AuctionCard instead
  if (product.auctionId || product.auction) {
    return (
      <AuctionCard
        product={product}
        onAddToCart={onAddToCart}
        onToggleFavorite={onToggleFavorite}
        onViewDetails={onViewDetails}
        onShare={onShare}
        isFavorite={isFavorite}
        isOwnProduct={isOwnProduct}
        variant={variant}
        showSeller={showSeller}
        navigationState={navigationState}
      />
    );
  }

  const purchaseType = purchaseTypeUi;

  // If this is a job, use JobCard instead
  if (product.isJob) {
    const jobData = {
      id: product.id,
      title: product.name,
      company: product.company || product.seller.name,
      companyLogo: product.companyLogo || product.seller.avatar,
      location: product.location || product.seller.location,
      salary: product.salary,
      employmentType: product.employmentType || 'full-time',
      experienceLevel: product.experienceLevel || 'mid',
      isRemote: product.isRemote || false,
      postedDate: product.postedDate || new Date().toISOString(),
      description: product.description,
      requirements: product.requirements,
      benefits: product.benefits,
      tags: product.tags,
      isNew: product.isNew,
      isFeatured: product.isFeatured,
      isUrgent: product.isUrgent,
      applicantsCount: product.applicantsCount,
      viewsCount: product.viewsCount,
      sellerId: product.sellerId, // Add sellerId from the product
    };

    return (
      <JobCard
        job={jobData}
        onApply={onAddToCart}
        onToggleFavorite={onToggleFavorite}
        onViewDetails={onViewDetails}
        onShare={onShare}
        isFavorite={isFavorite}
        isOwnJob={isOwnProduct}
        variant={variant}
      />
    );
  }

  const discountPercentage = product.discount || 
    (product.originalPrice ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100) : 0);

  const handleAddToCart = (e: React.MouseEvent) => {
    e.stopPropagation();
    // If this is a private single buy product, open the offer popup
    if (isOfferProduct) {
      setOfferPopupOpen(true);
    } else {
      onAddToCart?.(product.id);
    }
  };

  const handleOfferSuccess = () => {
    // Optionally show a success notification or refresh data
    console.log('Offer created successfully');
  };

  const handleToggleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleFavorite?.(product.id);
  };

  const handleViewDetails = () => {
    onViewDetails?.(product.id, navigationState);
  };

  const handleShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    onShare?.(product.id);
  };

  if (variant === 'compact') {
    return (
      <>
        <motion.div
          whileHover={{ y: -2 }}
          whileTap={{ scale: 0.99 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
        >
          <Card
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              cursor: 'pointer',
              borderRadius: 2,
              boxShadow: '0 6px 20px rgba(15, 23, 42, 0.08)',
              border: '1px solid rgba(148, 163, 184, 0.35)',
              '&:hover': {
                boxShadow: '0 10px 28px rgba(15, 23, 42, 0.14)',
                borderColor: 'rgba(148, 163, 184, 0.6)',
                '& .add-to-cart-button': {
                  opacity: 1,
                },
              },
              transition: 'all 0.2s ease',
              p: 2,
              mb: 1,
            }}
            onClick={handleViewDetails}
          >
          {/* Product Image */}
          <Box sx={{ 
            width: 200, 
            height: 200, 
            flexShrink: 0,
            mr: 3,
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 2,
            bgcolor: '#f8fafc'
          }}>
            <img
              src={product.image}
              alt={product.name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transition: 'transform 0.2s ease',
              }}
            />
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background:
                  'linear-gradient(180deg, rgba(0,0,0,0.00) 45%, rgba(0,0,0,0.22) 100%)',
                pointerEvents: 'none',
                zIndex: 1,
              }}
            />
          {/* Image count badge */}
          {Array.isArray((product as any).images) && (product as any).images.length >= 1 && (
            <Box
              sx={{
                position: 'absolute',
                right: 8,
                bottom: 10,
                bgcolor: 'rgba(0,0,0,0.6)',
                color: 'white',
                borderRadius: 999,
                px: 1,
                py: 0.25,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                backdropFilter: 'blur(6px)',
                border: '1px solid rgba(255,255,255,0.2)',
                zIndex: 3,
                pointerEvents: 'none'
              }}
            >
              <PhotoCamera sx={{ fontSize: 16, color: 'white' }} />
              <Typography variant="caption" sx={{ lineHeight: 1, fontWeight: 600 }}>
                {(product as any).images.length}
              </Typography>
            </Box>
          )}
            <Chip
              icon={purchaseType.icon}
              label={purchaseType.label}
              size="small"
              sx={{
                position: 'absolute',
                top: 10,
                left: 10,
                zIndex: 4,
                height: 28,
                px: 0.5,
                fontSize: '0.76rem',
                fontWeight: 800,
                letterSpacing: '0.2px',
                color: purchaseType.color,
                background: purchaseType.bgcolor,
                boxShadow: '0 10px 24px rgba(15, 23, 42, 0.18)',
                border: '1px solid rgba(255,255,255,0.35)',
                backdropFilter: 'blur(10px)',
                '& .MuiChip-icon': { ml: 0.5 },
                '& .MuiChip-label': { px: 0.75 },
              }}
            />
            {discountPercentage > 0 && (
              <Chip
                icon={<LocalOffer sx={{ fontSize: 18, color: 'white' }} />}
                label={`-${discountPercentage}%`}
                size="small"
                sx={{
                  position: 'absolute',
                  bottom: 10,
                  left: 10,
                  zIndex: 4,
                  height: 32,
                  px: 0.75,
                  fontSize: '0.9rem',
                  fontWeight: 900,
                  letterSpacing: '0.3px',
                  color: 'white',
                  background: 'linear-gradient(135deg, rgba(239,68,68,1) 0%, rgba(236,72,153,1) 100%)',
                  boxShadow: '0 12px 26px rgba(239, 68, 68, 0.35)',
                  border: '1px solid rgba(255,255,255,0.28)',
                  '& .MuiChip-icon': { ml: 0.5 },
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            )}
            {product.isNew && (
              <Chip
                icon={<NewReleases sx={{ fontSize: 18, color: '#064e3b' }} />}
                label={t('productCard.new')}
                size="small"
                sx={{
                  position: 'absolute',
                  right: 10,
                  bottom: Array.isArray((product as any).images) && (product as any).images.length >= 1 ? 44 : 10,
                  zIndex: 4,
                  height: 28,
                  px: 0.5,
                  fontSize: '0.78rem',
                  fontWeight: 900,
                  letterSpacing: '0.2px',
                  color: '#064e3b',
                  background: 'linear-gradient(135deg, rgba(34,197,94,0.25) 0%, rgba(16,185,129,0.40) 100%)',
                  boxShadow: '0 10px 24px rgba(16, 185, 129, 0.18)',
                  border: '1px solid rgba(16,185,129,0.35)',
                  backdropFilter: 'blur(10px)',
                  '& .MuiChip-icon': { ml: 0.5 },
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            )}
            {isOwnProduct && (
              <Chip
                label={t('productCard.myProduct')}
                size="small"
                sx={{
                  position: 'absolute',
                  top: 44,
                  left: 10,
                  fontSize: '0.7rem',
                  fontWeight: 'bold',
                  height: 20,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                  }
                }}
              />
            )}
          </Box>

          {/* Product Info */}
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            {/* Product Name */}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
                fontSize: '1.1rem',
                lineHeight: 1.3,
                mb: 1,
                color: '#0066cc',
                cursor: 'pointer',
                '&:hover': {
                  color: '#c45500',
                  textDecoration: 'underline'
                },
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {product.name}
            </Typography>

            {/* Rating */}
            {isOfferProduct ? null : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Rating 
                    value={product.rating} 
                    precision={0.1} 
                    size="small" 
                    readOnly 
                    sx={{
                      '& .MuiRating-icon': {
                        fontSize: '1rem'
                      }
                    }}
                  />
                  <Typography variant="body2" sx={{ ml: 0.5, color: '#0066cc' }}>
                    {product.rating.toFixed(1)}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  ({product.reviewCount.toLocaleString()})
                </Typography>
              </Box>
            )}

            {/* Price */}
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 2 }}>
              <Typography variant="h5" fontWeight="bold" color="#B12704">
                ${typeof product.price === 'number' ? product.price.toFixed(2) : product.price}
              </Typography>
              {product.originalPrice && (
                <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                  ${typeof product.originalPrice === 'number' ? product.originalPrice.toFixed(2) : product.originalPrice}
                </Typography>
              )}
            </Box>

            {/* Features/Tags */}
            {product.tags && product.tags.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                {product.tags.slice(0, 3).map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag}
                    size="small"
                    variant="outlined"
                    sx={{ 
                      fontSize: '0.7rem', 
                      height: 20,
                      borderColor: '#ddd',
                      color: '#666'
                    }}
                  />
                ))}
              </Box>
            )}

            {/* Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 'auto' }}>
              {!isOwnProduct && (
                <Button
                  className="add-to-cart-button"
                  variant="contained"
                  size="medium"
                  startIcon={isOfferProduct ? <Message /> : <ShoppingCart />}
                  onClick={handleAddToCart}
                  sx={{
                    bgcolor: isOfferProduct
                      ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`
                      : '#ffd814',
                    color: isOfferProduct ? '#fff' : '#000',
                    fontWeight: 'bold',
                    borderRadius: isOfferProduct ? 999 : 2,
                    px: isOfferProduct ? (isMobile ? 4.25 : 3.75) : (isMobile ? 3.5 : 3),
                    py: isOfferProduct ? (isMobile ? 1.35 : 1.15) : (isMobile ? 1.25 : 1),
                    textTransform: 'none',
                    fontSize: isOfferProduct ? (isMobile ? '1.05rem' : '0.98rem') : (isMobile ? '1rem' : '0.9rem'),
                    letterSpacing: isOfferProduct ? '0.2px' : undefined,
                    opacity: 0,
                    transition: 'opacity 0.2s ease',
                    '&:hover': {
                      bgcolor: isOfferProduct
                        ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`
                        : '#f7ca00',
                      transform: isOfferProduct ? 'translateY(-1px)' : undefined,
                    },
                    boxShadow: isOfferProduct ? `0 12px 30px ${theme.palette.primary.main}40` : 'none',
                    border: isOfferProduct ? 'none' : '1px solid #f0c14b',
                    '&:active': {
                      transform: isOfferProduct ? 'translateY(0px) scale(0.98)' : undefined,
                    },
                  }}
                >
                  {isOfferProduct ? (t('productCard.makeOffer') || 'Make Offer') : t('productCard.addToCart')}
                </Button>
              )}
              
              <IconButton 
                onClick={handleToggleFavorite} 
                sx={{
                  color: isFavorite ? '#ff6b6b' : '#666',
                  width: isMobile ? 44 : undefined,
                  height: isMobile ? 44 : undefined,
                  '& svg': { fontSize: isMobile ? 26 : undefined },
                  '&:hover': {
                    bgcolor: 'rgba(255, 107, 107, 0.1)',
                  }
                }}
              >
                {isFavorite ? <Favorite /> : <FavoriteBorder />}
              </IconButton>

              <IconButton 
                onClick={handleShare}
                sx={{
                  color: '#666',
                  width: isMobile ? 44 : undefined,
                  height: isMobile ? 44 : undefined,
                  '& svg': { fontSize: isMobile ? 26 : undefined },
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.1)',
                  }
                }}
              >
                <Share />
              </IconButton>
            </Box>
          </Box>
        </Card>
      </motion.div>
      <NewOfferPopup
        open={offerPopupOpen}
        onClose={() => setOfferPopupOpen(false)}
        product={{
          id: product.id,
          name: product.name,
          price: product.price,
          image: product.image,
          sellerId: product.sellerId,
          seller: {
            name: product.seller.name,
            avatar: product.seller.avatar,
          },
        }}
        onSuccess={handleOfferSuccess}
      />
      </>
    );
  }

  return (
    <>
      <motion.div
        whileHover={{ y: -4 }}
        whileTap={{ scale: 0.98 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
      >
        <Card
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            cursor: 'pointer',
            borderRadius: 2,
            boxShadow: '0 10px 30px rgba(15, 23, 42, 0.10)',
            border: '1px solid rgba(148, 163, 184, 0.35)',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              boxShadow: '0 18px 44px rgba(15, 23, 42, 0.16)',
              borderColor: 'rgba(148, 163, 184, 0.60)',
              '& .add-to-cart-button': {
                opacity: 1,
              },
            },
            transition: 'all 0.2s ease',
          }}
          onClick={handleViewDetails}
        >
        {/* Product Image */}
        <Box sx={{ position: 'relative' }}>
          <CardMedia
            component="img"
            height={variant === 'detailed' ? 380 : 340}
            image={product.image}
            alt={product.name}
            sx={{
              objectFit: 'cover',
              transition: 'transform 0.3s ease',
              '&:hover': {
                transform: 'scale(1.05)',
              },
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background:
                'linear-gradient(180deg, rgba(0,0,0,0.00) 50%, rgba(0,0,0,0.25) 100%)',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          />
          {/* Image count badge (default variant) */}
          {Array.isArray((product as any).images) && (product as any).images.length >= 1 && (
            <Box
              sx={{
                position: 'absolute',
                right: 8,
                bottom: 10,
                bgcolor: 'rgba(0,0,0,0.6)',
                color: 'white',
                borderRadius: 999,
                px: 1,
                py: 0.25,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                backdropFilter: 'blur(6px)',
                border: '1px solid rgba(255,255,255,0.2)',
                zIndex: 3,
                pointerEvents: 'none'
              }}
            >
              <PhotoCamera sx={{ fontSize: 16, color: 'white' }} />
              <Typography variant="caption" sx={{ lineHeight: 1, fontWeight: 600 }}>
                {(product as any).images.length}
              </Typography>
            </Box>
          )}
          
          {/* Badges */}
          <Chip
            icon={purchaseType.icon}
            label={purchaseType.label}
            size="small"
            sx={{
              position: 'absolute',
              top: 12,
              left: 12,
              zIndex: 4,
              height: 30,
              px: 0.5,
              fontSize: '0.8rem',
              fontWeight: 900,
              letterSpacing: '0.2px',
              color: purchaseType.color,
              background: purchaseType.bgcolor,
              boxShadow: '0 12px 26px rgba(15, 23, 42, 0.20)',
              border: '1px solid rgba(255,255,255,0.35)',
              backdropFilter: 'blur(10px)',
              '& .MuiChip-icon': { ml: 0.5 },
              '& .MuiChip-label': { px: 0.9 },
            }}
          />
          {discountPercentage > 0 && (
            <Chip
              icon={<LocalOffer sx={{ fontSize: 18, color: 'white' }} />}
              label={`-${discountPercentage}%`}
              size="small"
              sx={{
                position: 'absolute',
                bottom: 12,
                left: 12,
                zIndex: 4,
                height: 34,
                px: 0.75,
                fontSize: '0.95rem',
                fontWeight: 900,
                letterSpacing: '0.3px',
                color: 'white',
                background: 'linear-gradient(135deg, rgba(239,68,68,1) 0%, rgba(236,72,153,1) 100%)',
                boxShadow: '0 16px 34px rgba(239, 68, 68, 0.35)',
                border: '1px solid rgba(255,255,255,0.28)',
                '& .MuiChip-icon': { ml: 0.5 },
                '& .MuiChip-label': { px: 0.85 },
              }}
            />
          )}
          {product.isNew && (
            <Chip
              icon={<NewReleases sx={{ fontSize: 18, color: '#064e3b' }} />}
              label={t('productCard.new')}
              size="small"
              sx={{
                position: 'absolute',
                right: 12,
                bottom: Array.isArray((product as any).images) && (product as any).images.length >= 1 ? 46 : 12,
                zIndex: 4,
                height: 30,
                px: 0.5,
                fontSize: '0.82rem',
                fontWeight: 900,
                letterSpacing: '0.2px',
                color: '#064e3b',
                background: 'linear-gradient(135deg, rgba(34,197,94,0.22) 0%, rgba(16,185,129,0.42) 100%)',
                boxShadow: '0 12px 28px rgba(16, 185, 129, 0.18)',
                border: '1px solid rgba(16,185,129,0.35)',
                backdropFilter: 'blur(10px)',
                '& .MuiChip-icon': { ml: 0.5 },
                '& .MuiChip-label': { px: 0.85 },
              }}
            />
          )}
          <Box sx={{ position: 'absolute', top: 50, left: 12, display: 'flex', flexDirection: 'column', gap: 1, zIndex: 4, pointerEvents: 'none' }}>
            {isOwnProduct && (
              <Chip
                label={t('productCard.myProduct')}
                size="small"
                sx={{
                  fontWeight: 'bold',
                  fontSize: '0.7rem',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                  }
                }}
              />
            )}
            {product.isFeatured && (
              <Chip
                label={t('productCard.featured')}
                size="small"
                color="primary"
                sx={{ fontWeight: 'bold', fontSize: '0.7rem' }}
              />
            )}
          </Box>

          {/* Action Buttons */}
          <Box sx={{ position: 'absolute', top: 12, right: 12, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Tooltip title={isFavorite ? t('productCard.tooltip.removeFromFavorites') : t('productCard.tooltip.addToFavorites')}>
              <IconButton
                onClick={handleToggleFavorite}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.9)',
                  color: isFavorite ? 'error.main' : 'text.secondary',
                  width: isMobile ? 46 : undefined,
                  height: isMobile ? 46 : undefined,
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,1)',
                  },
                }}
                size={isMobile ? 'medium' : 'small'}
              >
                {isFavorite ? <Favorite sx={{ fontSize: isMobile ? 24 : 20 }} /> : <FavoriteBorder sx={{ fontSize: isMobile ? 24 : 20 }} />}
              </IconButton>
            </Tooltip>
            <Tooltip title={t('productCard.tooltip.share')}>
              <IconButton
                onClick={handleShare}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.9)',
                  width: isMobile ? 46 : undefined,
                  height: isMobile ? 46 : undefined,
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,1)',
                  },
                }}
                size={isMobile ? 'medium' : 'small'}
              >
                <Share sx={{ fontSize: isMobile ? 24 : 20 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Product Content */}
        <CardContent sx={{ flex: 1, px: 1.25, pt: 1, pb: 0, display: 'flex', flexDirection: 'column', '&:last-child': { pb: 0 } }}>

          {/* Product Name */}
          <Typography
            variant="h6"
            sx={{
              fontWeight: 500,
              fontSize: '0.95rem',
              lineHeight: 1.3,
              mb: 0.35,
              color: '#0066cc',
              cursor: 'pointer',
              '&:hover': {
                color: '#c45500',
                textDecoration: 'underline'
              },
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {product.name}
          </Typography>

          {/* Description (for detailed variant) */}
          {variant === 'detailed' && product.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 2,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                lineHeight: 1.4,
              }}
            >
              {product.description}
            </Typography>
          )}

          {/* Rating */}
          {isOfferProduct ? null : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Rating 
                  value={product.rating} 
                  precision={0.1} 
                  size="small" 
                  readOnly 
                  sx={{
                    '& .MuiRating-icon': {
                      fontSize: '0.9rem'
                    }
                  }}
                />
                <Typography variant="body2" sx={{ ml: 0.5, color: '#0066cc' }}>
                  {product.rating}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                ({product.reviewCount.toLocaleString()})
              </Typography>
            </Box>
          )}

          {/* Seller Info */}
          {showSeller && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Avatar
                src={product.seller.avatar}
                sx={{ width: 24, height: 24 }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" fontWeight="medium" noWrap>
                  {product.seller.name}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationOn sx={{ fontSize: 12, color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {product.seller.location || t('common.unknownLocation')}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {/* Category paths (nice labels) + Tags */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            {(() => {
              const paths: string[] = Array.isArray((product as any).categories) ? (product as any).categories : [];
              const clean = paths.filter(p => typeof p === 'string' && p.trim().length > 0);
              if (clean.length > 0) {
                return (
                  <>
                    {clean.slice(0, 2).map((p, idx) => {
                      const segs = p.split(' / ').map(s => s.trim()).filter(Boolean);
                      const [main, sub, subsub] = [segs[0] || '', segs[1] || '', segs[2] || ''];
                      const to = `/all-products?category=${encodeURIComponent((main || '').toLowerCase())}`
                        + (sub ? `&sub=${encodeURIComponent(sub)}` : '')
                        + (subsub ? `&subsub=${encodeURIComponent(subsub)}` : '');
                      return (
                        <Chip
                          key={`path-${idx}`}
                          label={p}
                          size="small"
                          variant="outlined"
                          onClick={(e) => { e.stopPropagation(); navigate(to); }}
                          sx={{ fontSize: '0.68rem', height: 20, cursor: 'pointer', borderColor: '#ddd', color: '#444' }}
                        />
                      );
                    })}
                  </>
                );
              }
              const cat = (product.category || '').trim();
              if (cat && cat.toLowerCase() !== 'unknown') {
                return (
                  <Chip
                    label={cat}
                    size="small"
                    variant="outlined"
                    color="primary"
                    sx={{ fontSize: '0.7rem', height: 22 }}
                  />
                );
              }
              return null;
            })()}
            {product.tags && product.tags.length > 0 && (
              product.tags.slice(0, 3).map((tag, index) => (
                <Chip
                  key={index}
                  label={tag}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.65rem', height: 20 }}
                />
              ))
            )}
          </Box>

          {/* Price + Inline Action */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
              <Typography variant="h6" fontWeight="bold" color="#B12704" sx={{ fontSize: '1.1rem' }}>
                ${typeof product.price === 'number' ? product.price.toFixed(2) : product.price}
              </Typography>
              {product.originalPrice && (
                <Typography variant="body2" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                  ${typeof product.originalPrice === 'number' ? product.originalPrice.toFixed(2) : product.originalPrice}
                </Typography>
              )}
            </Box>
            {!isOwnProduct && (
              <Button
                className="add-to-cart-button"
                variant="contained"
                size="small"
                startIcon={isOfferProduct ? <Message /> : <ShoppingCart />}
                onClick={handleAddToCart}
                sx={{
                  bgcolor: isOfferProduct
                    ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`
                    : '#ffd814',
                  color: isOfferProduct ? '#fff' : '#000',
                  fontWeight: 'bold',
                  borderRadius: isOfferProduct ? 999 : 1.25,
                  py: isOfferProduct ? 0.75 : 0.5,
                  px: isOfferProduct ? 2.0 : 1.25,
                  mb: 0.75,
                  textTransform: 'none',
                  fontSize: isOfferProduct ? '0.95rem' : '0.85rem',
                  letterSpacing: isOfferProduct ? '0.2px' : undefined,
                  opacity: 0,
                  transition: 'opacity 0.2s ease',
                  minHeight: 32,
                  lineHeight: 1.2,
                  '&:hover': {
                    bgcolor: isOfferProduct
                      ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`
                      : '#f7ca00',
                    transform: isOfferProduct ? 'translateY(-1px)' : undefined,
                  },
                  boxShadow: isOfferProduct ? `0 10px 24px ${theme.palette.primary.main}3d` : 'none',
                  border: isOfferProduct ? 'none' : '1px solid #f0c14b',
                  '&:active': {
                    transform: isOfferProduct ? 'translateY(0px) scale(0.98)' : undefined,
                  },
                }}
              >
                {isOfferProduct ? (t('productCard.makeOffer') || 'Make Offer') : t('productCard.addToCart')}
              </Button>
            )}
          </Box>
        </CardContent>

      </Card>
    </motion.div>
    <NewOfferPopup
      open={offerPopupOpen}
      onClose={() => setOfferPopupOpen(false)}
      product={{
        id: product.id,
        name: product.name,
        price: product.price,
        image: product.image,
        sellerId: product.sellerId,
        seller: {
          name: product.seller.name,
          avatar: product.seller.avatar,
        },
      }}
      onSuccess={handleOfferSuccess}
    />
    </>
  );
};

export default ProductCard;
